import React, {useEffect, useRef, useState} from 'react';
import {Table, Spinner, Row, Col, Button} from 'react-bootstrap';
import {deleteLoadCell, deleteVfd} from 'api/DevicesApi';
import ModalDialog from 'components/Dialogs/ModalDialog';
import {capacityUnits, loadCellClasses, loadCellOutputs, vfdClasses} from 'constants/dataConstants';
import {useAppContext} from 'contexts/AppContext';
import {isSystemAdmin} from 'utils/Auth';
import {arrayToObject, isEmpty} from 'utils/Utils';

const inheritedModbus = {
    textDecoration: 'underline dotted',
};

export default function DevicesList({
    pageState, currentVersion, currentConfiguration, motions, motors, onDataUpdate,
    onLoadCellDetailsClick, onVfdDetailsClick, onAddLoadCellButtonClick, onAddVfdButtonClick
}) {
    const isMounted = useRef(false);
    const deviceObject = useRef(null);
    const deviceType = useRef(null);
    const {showMessage, handleApiError} = useAppContext();
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogBody, setDeleteDialogBody] = useState(<></>);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [loadCells, setLoadCells] = useState({});
    const [vfds, setVfds] = useState({});
    const isEditable = isSystemAdmin() && currentVersion.status === 0;

    useEffect(() => {
        isMounted.current = true;
        getDevices();
        return () => isMounted.current = false;
    }, [pageState, currentVersion, currentConfiguration]);

    const getDevices = () => {
        if (isMounted.current) {
            setIsLoading(true);
            if (!isEmpty(currentConfiguration) && !isEmpty(currentConfiguration.crane_details)) {
                setLoadCells(currentConfiguration.crane_details.load_cells);
                setVfds(arrayToObject(currentConfiguration.crane_details.vfd_config));
            }
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (device, type) => {
        deviceObject.current = device;
        deviceType.current = type;
        const name = device.name || device.vfd_name;
        setDeleteDialogBody(<p>Do you really want to delete the {type} "<b>{name}</b>"?</p>);
        setShowDeleteDialog(true);
    };

    const handleModalClosed = () => {
        setShowDeleteDialog(false);
    };

    const handleModalConfirmed = () => {
        setIsLoading(true);
        const onDone = () => isMounted.current && setIsLoading(false);
        const fn = deviceType.current === 'VFD' ? deleteVfd : deleteLoadCell;
        fn(pageState.craneId, deviceObject.current.id, handleSuccessResponse, handleApiError, onDone);
        handleModalClosed();
    };

    const handleSuccessResponse = (response) => {
        if (isMounted.current) {
            showMessage('success', 'Devices configuration has been successfully saved');
            onDataUpdate();
        }
    };

    let addLoadCellButton;
    if (isEditable) addLoadCellButton = <button className="btn btn-add" onClick={onAddLoadCellButtonClick}>Add</button>;

    let loadCellsTableContent;
    if (isLoading) loadCellsTableContent = <tr><td colSpan="10" className="text-center"><Spinner animation="border" /></td></tr>;
    else if (isEmpty(loadCells)) loadCellsTableContent = <tr><td colSpan="10">No load cells found</td></tr>;
    else {
        loadCellsTableContent = Object.entries(loadCells).map(([loadCellUuid, loadCell]) => {
            let modbusAddress = loadCell.output === 2 ? loadCell.modbus_connection.address : vfds[loadCell.vfd_id]?.modbus_connection.address;
            if (loadCell.output === 1) modbusAddress = <span style={inheritedModbus}>{modbusAddress}</span>;
            return (
                <tr key={loadCellUuid}>
                    <td>{loadCell.name}</td>
                    <td>{motions[loadCell.motion_id]?.motion_name}</td>
                    <td>{loadCellClasses[loadCell.lc_class]}</td>
                    <td className="text-center">{capacityUnits[loadCell.unit]}</td>
                    <td className="text-center">{loadCellOutputs[loadCell.output]}</td>
                    <td>{loadCell.output === 1 ? vfds[loadCell.vfd_id]?.vfd_name : ''}</td>
                    <td className="text-center">{modbusAddress}</td>
                    <td className="text-center">{loadCell.modbus_register}</td>
                    <td className="text-center">{loadCell.active ? 'Yes' : 'No'}</td>
                    <td className="text-center">
                        <span className="text-primary text-link" onClick={() => onLoadCellDetailsClick(loadCell)}>{isEditable ? 'Edit' : 'View'}</span>
                    </td>
                    {isEditable &&
                        <td className="text-center">
                            <span className="text-primary text-link" onClick={() => handleDeleteClick(loadCell, 'load cell')}>Delete</span>
                        </td>}
                </tr>
            );
        });
    }

    let addVfdButton;
    if (isEditable) addVfdButton = <button className="btn btn-add" onClick={onAddVfdButtonClick}>Add</button>;

    let vfdsTableContent;
    if (isLoading) vfdsTableContent = <tr><td colSpan="8" className="text-center"><Spinner animation="border" /></td></tr>;
    else if (isEmpty(vfds)) vfdsTableContent = <tr><td colSpan="8">No VFDs found</td></tr>;
    else {
        vfdsTableContent = Object.entries(vfds).map(([vfdUuid, vfd]) => {
            let vfdMotors = vfd.vfd_motor_mapping ? vfd.vfd_motor_mapping.map(item => motors[item].name).join(', ') : '-';
            return (
                <tr key={vfdUuid}>
                    <td>{vfd.vfd_name}</td>
                    <td>{motions[vfd.motion_reference].motion_name}</td>
                    <td>{vfdClasses[vfd.vfd_class]}</td>
                    <td className="text-center">{vfd.modbus_connection ? vfd.modbus_connection.address : '-'}</td>
                    <td className="text-center">{vfd.current_rated}</td>
                    <td>{vfdMotors}</td>
                    <td className="text-center">{vfd.active ? 'Yes' : 'No'}</td>
                    <td className="text-center">
                        <span className="text-primary text-link" onClick={() => onVfdDetailsClick(vfd)}>{isEditable ? 'Edit' : 'View'}</span>
                    </td>
                    {isEditable &&
                        <td className="text-center">
                            <span className="text-primary text-link" onClick={() => handleDeleteClick(vfd, 'VFD')}>Delete</span>
                        </td>}
                </tr>
            );
        });
    }

    return (
        <>
            <ModalDialog title="Deleting device" body={deleteDialogBody} show={showDeleteDialog} onHide={handleModalClosed}>
                <Button variant="secondary" onClick={handleModalClosed}>No</Button>
                <Button variant="danger" onClick={handleModalConfirmed}>Yes</Button>
            </ModalDialog>
            <Row>
                <Col className="text-title title-header1" style={{paddingTop: '10px'}}>VFDs</Col>
                <Col className="text-right">{addVfdButton}</Col>
            </Row>
            <Table striped bordered hover size="sm">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Motion</th>
                        <th>Class</th>
                        <th className="text-center">Modbus address</th>
                        <th className="text-center">Current rated</th>
                        <th>Motors</th>
                        <th className="text-center">Active</th>
                        <th colSpan={isEditable ? 2 : 1} />
                    </tr>
                </thead>
                <tbody>{vfdsTableContent}</tbody>
            </Table>
            <Row>
                <Col className="text-title title-header1" style={{paddingTop: '10px'}}>Load cells</Col>
                <Col className="text-right">{addLoadCellButton}</Col>
            </Row>
            <Table striped bordered hover size="sm">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Motion</th>
                        <th>Class</th>
                        <th className="text-center">Unit</th>
                        <th className="text-center">Output</th>
                        <th>VFD</th>
                        <th className="text-center">Modbus address</th>
                        <th className="text-center">Modbus register</th>
                        <th className="text-center">Active</th>
                        <th colSpan={isEditable ? 2 : 1} />
                    </tr>
                </thead>
                <tbody>{loadCellsTableContent}</tbody>
            </Table>
        </>
    );
}
